import { useEffect, useState } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import { getStoredToken } from '@/lib/api';

// PR-UPLOAD-FIX MAY 11 — every file-serving endpoint on the api requires a
// JWT in the Authorization header, so plain <img src> hits the route without
// auth and gets a 401. AuthedImage fetches the bytes with the token, blob-
// URLs them, and renders <img> against the blob. construction/AuthImg.tsx
// is the older single-purpose twin of this; new code should import from here.

const API_BASE = (import.meta.env.VITE_API_URL as string) || 'https://api.jasrhub.com';

interface AuthedImageProps {
  /** Server-relative path (e.g. /api/v1/trips/123/photos/456) or absolute URL. */
  src: string | null | undefined;
  alt?: string;
  className?: string;
  /** Rendered while loading. */
  loadingFallback?: React.ReactNode;
  /** Rendered if src is empty or fetch failed. */
  emptyFallback?: React.ReactNode;
  onClick?: () => void;
  loading?: 'lazy' | 'eager';
  /** Forwarded to the rendered <img>, useful for zoom transforms in lightboxes. */
  style?: React.CSSProperties;
  draggable?: boolean;
}

export function AuthedImage({
  src, alt = '', className, loadingFallback, emptyFallback, onClick, loading = 'lazy', style, draggable,
}: AuthedImageProps) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [state, setState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');

  useEffect(() => {
    if (!src) { setBlobUrl(null); setState('idle'); return; }
    let cancelled = false;
    let objUrl: string | null = null;
    setState('loading');

    const jwt = getStoredToken();
    const fullUrl = src.startsWith('http') ? src : `${API_BASE}${src}`;
    fetch(fullUrl, { headers: jwt ? { Authorization: `Bearer ${jwt}` } : {} })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const blob = await r.blob();
        if (cancelled) return;
        objUrl = URL.createObjectURL(blob);
        setBlobUrl(objUrl);
        setState('ready');
      })
      .catch(() => { if (!cancelled) setState('error'); });

    return () => {
      cancelled = true;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
  }, [src]);

  if (!src || state === 'error') {
    return <>{emptyFallback ?? (
      <div className={className + ' flex items-center justify-center bg-muted/30 text-muted-foreground'}>
        <AlertCircle className="h-4 w-4" />
      </div>
    )}</>;
  }
  if (state !== 'ready' || !blobUrl) {
    return <>{loadingFallback ?? (
      <div className={className + ' flex items-center justify-center bg-muted/30 text-muted-foreground'}>
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    )}</>;
  }
  return <img src={blobUrl} alt={alt} className={className} loading={loading} onClick={onClick} style={style} draggable={draggable} />;
}

/**
 * Fetch an auth-protected file and open it in a new tab as a blob URL.
 * Works for any mime (image, pdf, …) — the browser picks the renderer.
 */
export async function viewFile(serverRelativeUrl: string): Promise<void> {
  const jwt = getStoredToken();
  const fullUrl = serverRelativeUrl.startsWith('http') ? serverRelativeUrl : `${API_BASE}${serverRelativeUrl}`;
  const res = await fetch(fullUrl, { headers: jwt ? { Authorization: `Bearer ${jwt}` } : {} });
  if (!res.ok) throw new Error(`Couldn't open file (HTTP ${res.status})`);
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  window.open(blobUrl, '_blank', 'noopener,noreferrer');
  // Browsers keep blob URLs alive until the page unloads; revoke after a
  // minute to be polite without breaking the open tab.
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}
